<template>
  <div class="ps-detail-page" :class="{ 'is-course-page': isTwoLevelPage }">
    <div class="ps-detail-top">
      <div class="ps-detail-top__crumb">
        <span class="ps-detail-top__link" @click="goHome">首页</span>
        <span class="ps-detail-top__sep">›</span>
        <span class="ps-detail-top__link" @click="goBack">{{
          config.title
        }}</span>
        <span class="ps-detail-top__sep">›</span>
        <span class="ps-detail-top__current">{{ headerTitle }}</span>
      </div>
      <button type="button" class="ps-detail-top__overview" @click="goBack">
        <img :src="iconOverview" alt="" />
        <span>项目总览</span>
      </button>
    </div>

    <ProjectDetailHeader
      :title="headerTitle"
      :status="project.auditState"
      :text="headerStatusText"
      :version="headerVersion"
      :meta-list="metaList"
      :show-update="showUpdate"
      @update="handleUpdate"
      @back="goBack"
    />

    <div class="ps-detail-card">
      <ProjectDetailTabs
        v-model="activeTab"
        :request-count="requestCount"
        :history-count="historyCount"
        @change="onTabChange"
      />

      <div class="ps-detail-body">
        <!-- 现状 = 原预览（直接内嵌，无需二次点击） -->
        <CurrentStatusPanel
          v-show="activeTab === 'current'"
          v-if="applyId"
          :key="'current-' + statusKey"
          :apply-id="applyId"
          :apply-type="applyType"
          :project="project"
        />

        <!-- 变更历史 = 已通过 + 已驳回 -->
        <ModuleChangeList
          v-show="activeTab === 'history'"
          v-if="applyId"
          ref="historyList"
          :apply-id="applyId"
          :apply-type="applyType"
          mode="history"
          @loaded="onHistoryLoaded"
        />

        <!-- 变更请求 = 待提交草稿 + 待审核 -->
        <ModuleChangeList
          v-show="activeTab === 'requests'"
          v-if="applyId"
          ref="requestsList"
          :apply-id="applyId"
          :apply-type="applyType"
          mode="requests"
          @loaded="onRequestsLoaded"
          @audited="onAudited"
        />
      </div>
    </div>
  </div>
</template>

<script>
import ProjectDetailHeader from "./ProjectDetailHeader.vue";
import ProjectDetailTabs from "./ProjectDetailTabs.vue";
import ModuleChangeList from "./ModuleChangeList.vue";
import CurrentStatusPanel from "./CurrentStatusPanel.vue";
import {
  getModuleConfigByApplyType,
  AUDIT_STATE,
  formatApplyVersion,
  getCourseAuditStateText,
  isTwoLevelApplyType,
} from "./moduleConfig.js";
import { getAction } from "@/api";
import iconOverview from "@/assets/images/projectShell/detail-icon-overview.svg";

export default {
  name: "ProjectDetail",
  components: {
    ProjectDetailHeader,
    ProjectDetailTabs,
    ModuleChangeList,
    CurrentStatusPanel,
  },
  data() {
    return {
      activeTab: "current",
      project: {},
      requestCount: 0,
      historyCount: 0,
      statusKey: 0,
      iconOverview,
    };
  },
  computed: {
    applyId() {
      return this.$route.query.id || "";
    },
    applyType() {
      return String(this.$route.query.applyType || "1");
    },
    config() {
      return getModuleConfigByApplyType(this.applyType);
    },
    isTwoLevelPage() {
      return isTwoLevelApplyType(this.applyType);
    },
    headerTitle() {
      return this.project.name || this.$route.query.name || this.config.title;
    },
    headerVersion() {
      return formatApplyVersion(this.project);
    },
    headerStatusText() {
      if (!this.isTwoLevelPage) return "";
      return getCourseAuditStateText(this.project);
    },
    metaList() {
      const p = this.project;
      const dateField = (this.config.cardFields || []).find(
        (f) => f.key === "updated"
      );
      const dateLabel = dateField ? dateField.label : "最近更新";
      const dateKeys =
        dateField && dateField.from
          ? String(dateField.from).split("|")
          : ["updateTime", "createTime", "foundTime"];
      let dateValue = "";
      for (let i = 0; i < dateKeys.length; i++) {
        const v = p[dateKeys[i]];
        if (v !== null && v !== undefined && v !== "") {
          dateValue = v;
          break;
        }
      }
      const list = [
        {
          label: "负责人",
          value: p.principals || "-",
          icon: "leader",
        },
        {
          label: "申报年份",
          value: p.applyYear || "-",
          icon: "year",
        },
        {
          label: "所属单位",
          value: p.belongDepartment || "-",
          icon: "org",
        },
        {
          label: dateLabel,
          value: dateValue || "-",
          icon: "update",
        },
        {
          label: "变更请求",
          value: `${this.requestCount} 条待处理`,
          warn: this.requestCount > 0,
          icon: "request",
        },
      ];
      // 教育厅/局只读角色（guide / education-role）不展示「变更请求：X 条待处理」
      const role = this.$store.state.role;
      if (role === "education-guide-role" || role === "education-role") {
        return list.filter((i) => i.label !== "变更请求");
      }
      return list;
    },
    showUpdate() {
      const role = this.$store.state.role;
      if (role !== "school-role" && role !== "teacher-role") return false;
      return String(this.project.auditState) === AUDIT_STATE.APPROVED;
    },
  },
  watch: {
    "$route.query": {
      immediate: true,
      handler() {
        this.initFromRoute();
      },
    },
  },
  methods: {
    initFromRoute() {
      const tab = this.$route.query.tab;
      // 只读教育侧角色（教育厅/局）仅保留「现状」标签，强制回退到 current
      const readonlyRole =
        this.$store.state.role === "education-guide-role" ||
        this.$store.state.role === "education-role";
      if (tab === "history" || tab === "requests" || tab === "current") {
        this.activeTab = readonlyRole ? "current" : tab;
      } else {
        this.activeTab = "current";
      }
      this.loadProject();
    },
    loadProject() {
      if (!this.applyId) return;
      getAction("/lx/lxApply/queryById", { id: this.applyId }).then((res) => {
        if (res.code === 200 || res.code === 0) {
          this.project = res.result || {};
        }
      });
    },
    onTabChange(tab) {
      this.activeTab = tab;
      this.$router.replace({
        query: { ...this.$route.query, tab },
      });
    },
    onHistoryLoaded({ count }) {
      this.historyCount = count || 0;
    },
    onRequestsLoaded({ count }) {
      this.requestCount = count || 0;
    },
    onAudited() {
      if (this.$refs.historyList) {
        this.$refs.historyList.load();
      }
      this.statusKey += 1;
      this.loadProject();
    },
    handleUpdate() {
      // 思政微课走独立更新页，不影响原填报页
      if (this.config.routeUpdate) {
        this.$router.push({
          name: this.config.routeUpdate,
          query: {
            id: this.applyId,
            applyType: this.applyType,
            name: this.project.name,
          },
        });
        return;
      }
      this.$router.push({
        name: this.config.routeUpload,
        query: {
          id: this.applyId,
          applyType: this.applyType,
          new: "edit",
        },
      });
    },
    goBack() {
      this.$router.push({ name: this.config.routeList });
    },
    goHome() {
      this.$router.push({ path: "/personalCenter" });
    },
  },
};
</script>

<style lang="scss" scoped>
.ps-detail-page {
  width: 100%;
  padding: 26px 18px 24px;
  box-sizing: border-box;
  font-family: "Microsoft YaHei", "微软雅黑", sans-serif;
}

.ps-detail-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 8px;
  min-height: 33px;

  &__crumb {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 4px;
    color: #8b8b8b;
    font-size: 14px;
    line-height: 20px;
  }

  &__link {
    cursor: pointer;
    color: #8b8b8b;
    &:hover {
      color: #2964d3;
    }
  }

  &__sep {
    color: #8b8b8b;
  }

  &__current {
    color: #8b8b8b;
  }

  &__overview {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    height: 33px;
    padding: 0 12px 0 13px;
    background: #fff;
    border: 1px solid #3870ed;
    border-radius: 4px;
    cursor: pointer;
    font-size: 16px;
    color: #000;
    font-family: inherit;
    flex-shrink: 0;

    img {
      width: 13px;
      height: 10px;
      object-fit: contain;
    }

    span {
      line-height: 1;
      transform: translateY(1px);
    }

    &:hover {
      background: #f5f8ff;
    }
  }
}

.ps-detail-card {
  margin-top: 17px;
  box-shadow: 0 0 8px 0 rgba(115, 155, 220, 0.35);
  background: rgba(255, 255, 255, 0.96);
  border-radius: 8px;
  border: 1px solid rgba(229, 229, 229, 0.96);
  padding: 12px 19px 20px;
  min-height: 400px;
}

.ps-detail-body {
  margin-top: 12px;
  border-top: 1px solid #e5e5e5;
  padding-top: 12px;
}

.ps-detail-page.is-course-page {
  font-size: 16px;
  ::v-deep .ps-detail-head__title {
    font-size: 22px;
  }
  ::v-deep .ps-detail-head__meta {
    font-size: 15px;
  }
  ::v-deep .ps-btn {
    font-size: 15px;
  }
  ::v-deep .ps-tabs__item {
    font-size: 16px;
  }
  ::v-deep .ps-tabs__num {
    font-size: 13px;
  }
  ::v-deep .ps-badge {
    font-size: 14px;
  }
  ::v-deep .ps-change-list__note {
    font-size: 15px;
  }
  ::v-deep .ps-change-group__title {
    font-size: 18px;
  }
  ::v-deep .ps-change-group__count {
    font-size: 14px;
  }
  ::v-deep .ps-req-card__title {
    font-size: 16px;
  }
  ::v-deep .ps-req-card__op {
    font-size: 14px;
  }
  ::v-deep .ps-req-card__desc {
    font-size: 15px;
  }
  ::v-deep .ps-req-card__time {
    font-size: 14px;
  }
  ::v-deep .ps-req-card__remark,
  ::v-deep .ps-req-card__hint {
    font-size: 15px;
  }
  ::v-deep .ps-req-field,
  ::v-deep .ps-req-field-more {
    font-size: 14px;
  }
  ::v-deep .ps-act-btn {
    font-size: 15px;
  }
}

</style>
